/**
 * 🧵 **The vefstaður in the world — his loom A, and what our code hangs on it
 * (D136, T290).** And 🪨 **the kljásteinar lying on the shore**, his stone B.
 *
 * 🎯 haugur: *"loom A and stone B"* (`Design/samples/177_*`). Both were made
 * from words by Rodin on Replicate and slimmed (`tools/slim-model.mjs`).
 *
 * 🔴 **The frame is the model; the warp, the cloth and the hanging stones are
 * drawn HERE** (D136), from the anchor's `weave` state, so everybody sees the
 * cloth grow down the loom without opening it:
 *
 * | `weave` | drawn |
 * |---|---|
 * | `bare` | the frame alone |
 * | `warped` | 48 warp threads from the top beam down, twelve stones at their foot |
 * | `ell1`–`ell5` | and the cloth, woven from the TOP down — a warp-weighted loom is woven downward and the cloth rolled on the top beam |
 *
 * 📏 **Measured off loom A** (`177_loom_a_slim.glb`, model units ≈ metres):
 * 1.90 wide × 1.73 tall × 0.52 deep; the uprights stand at x ≈ ±0.83 and
 * LEAN — the foot at z ≈ +0.17, the top at z ≈ −0.18. So the model's **−z is
 * the wall side**, and it is turned half a circle from the bed's turn.
 * ⚠️ **Uniform scale, never stretched**: a stretched model was one of the seven
 * faults the pit house's baked pictures found.
 *
 * 🔴 **The house pattern** (`rummesh.ts`): loaded once, cloned per loom, and
 * re-dressed as a `MeshLambertNodeMaterial` over its own colour map with the
 * same daylight and torch lift — **never the file's own PBR material**.
 */

import {
  Box3,
  BufferGeometry,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  Mesh,
  type Object3D,
  SRGBColorSpace,
  Vector3,
  type Texture,
} from 'three';
import { MeshLambertNodeMaterial } from 'three/webgpu';
import { texture, uniform, uv, vec3 } from 'three/tsl';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
  burFrame,
  CHUNK_SIZE,
  ellsShown,
  ELLS_PER_PIECE,
  isKljasteinn,
  isLoom,
  loomCentre,
  loomFacing,
  loomWeave,
  type BurFacing,
  type LoomWeave,
} from '@lofi/shared';
import { burDaylight } from './burmesh.ts';

/** A loom's anchor, in chunk-local cells. */
export interface LoomAt {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly facing: BurFacing;
  readonly weave: LoomWeave;
}

/** A kljásteinn lying on the shore, in chunk-local cells. */
export interface StoneAt {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/* ── what is measured off the models ─────────────────────────────────────── */

/** 📏 The loom is drawn this wide, in metres — 5 cm short of its two cells each side. */
const LOOM_WIDTH = 1.9;
/** 📏 A kljásteinn is about this wide (0.4–1.1 kg of basalt). */
export const STONE_WIDTH = 0.1;
/**
 * 📐 **Loom A, in its own units after centring** (bottom at 0, middle of x and
 * z at 0), scaled by {@link LOOM_WIDTH} / its width. Measured by height slices.
 */
export const FRAME = {
  /** Model units across, end to end of the top beam. */
  width: 1.896,
  /** Height of the frame. */
  height: 1.73,
  /** The top beam's underside, where the warp hangs from. */
  warpTop: 1.62,
  /** Where the warp ends and the stones hang, 40 cm off the floor — they HANG, never rest (the first bake sat them on the floor). */
  warpFoot: 0.42,
  /** The uprights' middle at the floor and at the top: they lean toward −z. */
  zAtFloor: 0.17,
  zAtTop: -0.18,
  /** Half the warp's width — inside the uprights, which stand at ±0.83. */
  warpHalf: 0.62,
  /** Half the frame's depth: its back, at the top beam, is this far behind its middle. */
  depthHalf: 0.26,
} as const;
/** How far the model's back (−z) sits from the wall face, in metres. */
const WALL_GAP = 0.03;

/** 🧵 How far the front and back sheds stand off the warp's middle, and the cloth in front of both. */
export const WARP_LIFT = 0.012;
export const CLOTH_LIFT = 0.02;

/** 🧵 Warp threads drawn, and how wide each is — a thread every 2.6 cm. */
const WARP_THREADS = 48;
const THREAD_WIDTH = 0.004;
/** 🧶 Undyed vaðmál and its warp — the sheep's own grey-white. */
const THREAD_COLOUR = '#a99f8c';
/**
 * 🧶 Two shades, ell by ell, so the ells woven can be COUNTED on the loom. ⚠️ The
 * first bake drew the cloth #b5aa95 and it read as white paper; undyed vaðmál
 * is the sheep's own grey-brown. 🎯 haugur, shown the second: *"make the cloth darker"*.
 */
const CLOTH_COLOURS = ['#5f574a', '#544c40'] as const;
/** 🪨 Kljásteinar on a loom: twelve, in two rows — the front and back sheds. */
const STONES_ON_LOOM = 12;

/** 🔦 Torch light added at full — the houses' own number. */
const TORCH_LIFT = 0.5;
const TORCH_STEPS = 4;

function setEmissive(material: MeshLambertNodeMaterial, node: unknown): void {
  (material as unknown as { emissiveNode: unknown }).emissiveNode = node;
}

/** The z of the uprights' middle at a height, in model units — they lean. */
function zAt(y: number): number {
  const t = y / FRAME.height;
  return FRAME.zAtFloor + (FRAME.zAtTop - FRAME.zAtFloor) * t;
}

/* ── the chunk scan ──────────────────────────────────────────────────────── */

/** Every loom anchor in one chunk's blocks. */
export function loomsIn(blocks: Uint16Array): LoomAt[] {
  const out: LoomAt[] = [];
  for (let i = 0; i < blocks.length; i++) {
    /* eslint-disable-next-line security/detect-object-injection --
       A loop counter reading a Uint16Array, as rummesh.ts does. */
    const id = blocks[i] ?? 0;
    if (!isLoom(id)) continue;
    out.push({
      x: i % CHUNK_SIZE,
      y: Math.floor(i / (CHUNK_SIZE * CHUNK_SIZE)),
      z: Math.floor(i / CHUNK_SIZE) % CHUNK_SIZE,
      facing: loomFacing(id) ?? 'north',
      weave: loomWeave(id),
    });
  }
  return out;
}

/** Every kljásteinn lying in one chunk. */
export function stonesIn(blocks: Uint16Array): StoneAt[] {
  const out: StoneAt[] = [];
  for (let i = 0; i < blocks.length; i++) {
    /* eslint-disable-next-line security/detect-object-injection --
       A loop counter reading a Uint16Array, as rummesh.ts does. */
    const id = blocks[i] ?? 0;
    if (!isKljasteinn(id)) continue;
    out.push({
      x: i % CHUNK_SIZE,
      y: Math.floor(i / (CHUNK_SIZE * CHUNK_SIZE)),
      z: Math.floor(i / CHUNK_SIZE) % CHUNK_SIZE,
    });
  }
  return out;
}

/* ── a model, loaded once ────────────────────────────────────────────────── */

/**
 * One .glb, centred with its bottom at 0 and scaled uniformly so it is
 * `width` across, then cloned per use. The rúm's loader, without the bed.
 */
class Template {
  private template: Object3D | null = null;
  private map: Texture | null = null;
  private readonly waiting: (() => void)[] = [];
  private readonly mats = new Map<number, MeshLambertNodeMaterial>();
  /** Model units → metres. */
  scale = 1;
  readonly stats = { ready: false, map: false, placed: 0, error: null as string | null };

  constructor(url: string, width: number, label: string) {
    new GLTFLoader()
      .loadAsync(url)
      .then((gltf) => {
        const scene = gltf.scene;
        scene.traverse((o) => {
          if (o instanceof Mesh && this.map === null) {
            this.map = (o.material as { map?: Texture | null }).map ?? null;
          }
        });
        const box = new Box3().setFromObject(scene);
        const size = box.getSize(new Vector3());
        const centre = box.getCenter(new Vector3());
        this.scale = width / Math.max(size.x, 1e-6);
        scene.position.set(-centre.x, -box.min.y, -centre.z);
        const scaled = new Group();
        scaled.add(scene);
        scaled.scale.setScalar(this.scale);
        const wrap = new Group();
        wrap.add(scaled);
        this.template = wrap;
        this.stats.ready = true;
        this.stats.map = this.map !== null;
        for (const fill of this.waiting.splice(0)) fill();
      })
      .catch((err: unknown) => {
        this.stats.error = err instanceof Error ? err.message : String(err);
        console.error(`${label}: the model did not load`, err);
      });
  }

  /** The material for `light` (0–1) of added torch light. */
  private materialFor(light: number): MeshLambertNodeMaterial | null {
    const step = Math.round(Math.min(1, Math.max(0, light)) * TORCH_STEPS);
    const had = this.mats.get(step);
    if (had) return had;
    if (this.map === null) return null;
    this.map.colorSpace = SRGBColorSpace;
    const tile = texture(this.map, uv());
    const colour = vec3(tile.r, tile.g, tile.b);
    const made = new MeshLambertNodeMaterial();
    made.colorNode = colour;
    setEmissive(
      made,
      colour.mul(burDaylight.mul(0.35).add(uniform((step / TORCH_STEPS) * TORCH_LIFT))),
    );
    this.mats.set(step, made);
    return made;
  }

  /** A dressed copy, or `null` until the file has loaded. */
  copy(light: number): Object3D | null {
    if (this.template === null) return null;
    const c = this.template.clone(true);
    const mat = this.materialFor(light);
    if (mat !== null) {
      c.traverse((o) => {
        if (o instanceof Mesh) o.material = mat;
      });
    }
    this.stats.placed += 1;
    return c;
  }

  /** Run `fill` now, or once the file has loaded. */
  whenReady(fill: () => void): void {
    if (this.template !== null) fill();
    else this.waiting.push(fill);
  }
}

/* ── the warp and the cloth, our own geometry ────────────────────────────── */

const plainMats = new Map<string, MeshLambertNodeMaterial>();

/** A flat colour, lit like the models: daylight and torch lift. */
function plainMaterial(colourHex: string, light: number): MeshLambertNodeMaterial {
  const step = Math.round(Math.min(1, Math.max(0, light)) * TORCH_STEPS);
  const key = `${colourHex}:${String(step)}`;
  const had = plainMats.get(key);
  if (had) return had;
  const hex = Number.parseInt(colourHex.slice(1), 16);
  const c = vec3(((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255);
  const made = new MeshLambertNodeMaterial();
  made.colorNode = c;
  made.side = DoubleSide;
  setEmissive(made, c.mul(burDaylight.mul(0.35).add(uniform((step / TORCH_STEPS) * TORCH_LIFT))));
  plainMats.set(key, made);
  return made;
}

/** A quad between two heights on the leaning plane, from x0 to x1, in model units. */
function pushQuad(
  pos: number[],
  x0: number,
  x1: number,
  yTop: number,
  yBottom: number,
  zLift: number,
): void {
  const zt = zAt(yTop) + zLift;
  const zb = zAt(yBottom) + zLift;
  pos.push(x0, yTop, zt, x0, yBottom, zb, x1, yBottom, zb);
  pos.push(x0, yTop, zt, x1, yBottom, zb, x1, yTop, zt);
}

function geometryOf(pos: number[]): BufferGeometry {
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
}

/** 🧵 The warp: thin strips from the top beam to the stones, all in one mesh. */
const warpGeometry = ((): BufferGeometry => {
  const pos: number[] = [];
  for (let i = 0; i < WARP_THREADS; i++) {
    const x = -FRAME.warpHalf + ((2 * FRAME.warpHalf) / (WARP_THREADS - 1)) * i;
    /* The front and back sheds alternate, a hair apart. */
    const lift = i % 2 === 0 ? WARP_LIFT : -WARP_LIFT;
    pushQuad(pos, x - THREAD_WIDTH / 2, x + THREAD_WIDTH / 2, FRAME.warpTop, FRAME.warpFoot, lift);
  }
  return geometryOf(pos);
})();

/** 🧶 The `ell`th band of cloth (0 at the top beam), woven downward. Cached per band. */
const clothGeometries = new Map<number, BufferGeometry>();
function clothBand(ell: number): BufferGeometry {
  const had = clothGeometries.get(ell);
  if (had) return had;
  const pos: number[] = [];
  const band = (FRAME.warpTop - FRAME.warpFoot) / ELLS_PER_PIECE;
  const top = FRAME.warpTop - band * ell;
  /* A hair in FRONT of the front shed (+0.012): woven cloth hides its warp. The
     second bake drew the threads over the cloth as pale stripes. */
  pushQuad(pos, -FRAME.warpHalf - 0.01, FRAME.warpHalf + 0.01, top, top - band, CLOTH_LIFT);
  const g = geometryOf(pos);
  clothGeometries.set(ell, g);
  return g;
}

/** 🪨 Where the twelve stones hang, in model units: two staggered rows of six. */
export function stoneSpots(): { x: number; y: number; z: number }[] {
  const out: { x: number; y: number; z: number }[] = [];
  const per = STONES_ON_LOOM / 2;
  for (let row = 0; row < 2; row++)
    for (let i = 0; i < per; i++) {
      const x = -FRAME.warpHalf + 0.06 + ((2 * FRAME.warpHalf - 0.12) / (per - 1)) * i;
      const y = FRAME.warpFoot - 0.06 - row * 0.05;
      out.push({ x: x + (row === 0 ? 0 : 0.05), y, z: zAt(y) + (row === 0 ? 0.04 : -0.04) });
    }
  return out;
}

/* ── putting them in the world ───────────────────────────────────────────── */

/** A stable hash of a cell, 0–1, so a stone on the shore lies the same way for everybody. */
function hash01(x: number, y: number, z: number): number {
  let h = (x * 374761393 + y * 668265263 + z * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

export class LoomModels {
  private readonly frame: Template;
  private readonly stone: Template;
  readonly stats: { frame: Template['stats']; stone: Template['stats'] };

  constructor(frameUrl: string, stoneUrl: string) {
    this.frame = new Template(frameUrl, LOOM_WIDTH, 'Vefstaður');
    this.stone = new Template(stoneUrl, STONE_WIDTH, 'Kljásteinn');
    this.stats = { frame: this.frame.stats, stone: this.stone.stats };
  }

  /** One loom: the frame, and the warp, cloth and stones its picture asks for. */
  placeLoom(group: Group, loom: LoomAt, light: number): void {
    const { into } = burFrame(loom.facing);
    const c = loomCentre(loom, loom.facing);
    const holder = new Group();
    /* 📐 Against the back wall: the model's back (−z) at the wall face, which
       is half a cell beyond the anchor's centre along `into`. */
    this.frame.whenReady(() => {
      const s = this.frame.scale;
      const back = 0.5 - WALL_GAP - FRAME.depthHalf * s;
      holder.position.set(c.x + 0.5 + into.x * back, c.y, c.z + 0.5 + into.z * back);
      /* The bed's turn puts the model's +z along `into`; the loom's top leans
         to −z, toward the wall — so half a turn more. */
      holder.rotation.y = Math.atan2(into.x, into.z) + Math.PI;
      const frame = this.frame.copy(light);
      if (frame !== null) holder.add(frame);

      const shown = ellsShown(loom.weave);
      if (shown < 0) return;
      /* Our own geometry is in model units; scale it the way the frame is. */
      const ours = new Group();
      ours.scale.setScalar(s);
      ours.add(new Mesh(warpGeometry, plainMaterial(THREAD_COLOUR, light)));
      for (let ell = 0; ell < shown; ell++) {
        const colour = CLOTH_COLOURS[ell % 2] ?? CLOTH_COLOURS[0];
        ours.add(new Mesh(clothBand(ell), plainMaterial(colour, light)));
      }
      holder.add(ours);
      this.stone.whenReady(() => {
        for (const spot of stoneSpots()) {
          const st = this.stone.copy(light);
          if (st === null) continue;
          /* Hanging on its edge from a warp bundle, as a weight does. */
          st.rotation.x = Math.PI / 2;
          st.position.set(spot.x * s, spot.y * s, spot.z * s);
          holder.add(st);
        }
      });
    });
    group.add(holder);
  }

  /** 🪨 One stone lying on the sand, turned by its cell so it is not a pattern. */
  placeStone(group: Group, at: StoneAt, light: number): void {
    this.stone.whenReady(() => {
      const st = this.stone.copy(light);
      if (st === null) return;
      const r = hash01(at.x, at.y, at.z);
      st.position.set(at.x + 0.3 + r * 0.4, at.y, at.z + 0.7 - r * 0.4);
      st.rotation.y = r * Math.PI * 2;
      group.add(st);
    });
  }
}

/** One group per chunk, or `null` when it has no loom and no stone in it. */
export function buildLoomGroup(
  looms: readonly LoomAt[],
  stones: readonly StoneAt[],
  models: LoomModels,
  litAt: (x: number, y: number, z: number) => number = () => 0,
): Group | null {
  if (looms.length === 0 && stones.length === 0) return null;
  const group = new Group();
  /* 🔦 A loom's light is asked a cell IN FRONT of it, in the room: its own
     cells are solid, and a solid cell's light is 0. */
  for (const loom of looms) {
    const { into } = burFrame(loom.facing);
    models.placeLoom(group, loom, litAt(loom.x - into.x, loom.y, loom.z - into.z));
  }
  for (const stone of stones) models.placeStone(group, stone, litAt(stone.x, stone.y, stone.z));
  return group;
}
